<template>
  <div v-show="!isVoucherOpen">
    <button
      @click="openVoucherInput"
      class="voucher tw:font-bold tw:text-blue-700 tw:text-base tw:flex tw:items-center"
      data-testid="plus-checkout-add-voucher-code-button"
    >
      <TagIcon class="tw:mr-1 tw:fill-blue-700 tw:size-[18px]" />
      {{ $t('check_out_add_voucher') }}
    </button>
  </div>
  <div v-if="isVoucherOpen || voucherData?.isValid" class="tw:flex tw:items-end tw:justify-between">
    <div class="tw:flex tw:flex-col tw:w-full tw:mr-2">
      <p class="tw:font-bold tw:mb-1">
        {{ $t('check_out_voucher_field') }}<span v-if="required" class="tw:text-red-700">{{ '*' }}</span>
      </p>
      <BaseTextField
        name="voucher"
        aria-label="voucher"
        size="medium"
        type="text"
        class="tw:w-full tw:mr-2"
        dataTestId="plus-checkout-voucher-input"
        :disabled="voucherData?.voucherCode || (code.length > 0 && !props.price && !props.isLoading)"
        :class="getClass()"
        :value="code"
        :successValidation="voucherData?.isValid"
        :withLoader="false"
        @onChange="setVoucherCode"
        @keydown.enter="applyVoucherCode('apply')"
      />
    </div>
    <GhostButton
      size="medium"
      class="tw:whitespace-nowrap"
      dataTestId="plus-checkout-voucher-apply-button"
      @click="applyVoucherCode(voucherData === null ? 'apply' : 'remove')"
      :disabled="isLoading"
      >{{ voucherData === null ? $t('voucher_apply') : $t('voucher_remove') }}</GhostButton
    >
  </div>
  <div v-if="voucherData?.isValid" class="tw:mt-2 tw:text-green-700 tw:flex tw:items-center">
    <TagIcon class="tw:mr-1 tw:fill-green-700 tw:size-[18px]" />
    <p data-testid="plus-checkout-discount-success-message">
      {{
        $t('check_out_discount_success', {
          DiscountPercentage: formatPercentage(voucherData.discountPercentage, 0, 0),
          DiscountSavings: formatCurrency(Math.abs(props.discountAmount)),
        })
      }}
    </p>
  </div>
  <p class="tw:text-red-700" v-if="!voucherData?.isValid && voucherData?.invalidReason === 'VOUCHER_NOT_ACTIVE'">
    {{ $t('voucher_error_expired') }}
  </p>
  <p
    data-testid="plus-checkout-voucher-error-message"
    class="tw:text-red-700"
    v-if="!voucherData?.isValid && voucherData?.invalidReason === 'VOUCHER_NOT_FOUND'"
  >
    {{ $t('voucher_error') }}
  </p>
  <p class="tw:text-red-700" v-if="!price && code && !isLoading">
    {{ $t('something_went_wrong') }}
  </p>
</template>
<script setup>
import { ref } from 'vue'
import appAnalytics from '@/utils/tracking'

import GhostButton from '@/components/base/buttons/GhostButton.vue'
import BaseTextField from '@/components/base/inputs/BaseTextField.vue'
import TagIcon from '@/components/icons/TagIcon.svg?component'

import { selectedAccommodationId } from '@/layouts/queries'
import { useFormatting } from '@/composables/useFormatting'

const code = ref('')
const isVoucherOpen = ref(false)

const { formatCurrency, formatPercentage } = useFormatting()

const props = defineProps({
  voucherData: Object,
  discountAmount: Number,
  price: Object,
  isLoading: Boolean,
})

const emit = defineEmits(['applyVoucherCode'])
const applyVoucherCode = type => {
  if (type === 'apply') {
    appAnalytics.track(appAnalytics.events.SP_APPLY_VOUCHER, {
      item_id: selectedAccommodationId.value,
      promoCode: code.value,
    })
    emit('applyVoucherCode', code.value)
  }
  if (type === 'remove') {
    appAnalytics.track(appAnalytics.events.SP_REMOVE_VOUCHER, {
      item_id: selectedAccommodationId.value,
    })
    emit('applyVoucherCode', '')
    code.value = ''
  }
}

const setVoucherCode = e => {
  code.value = e
}
const openVoucherInput = () => {
  appAnalytics.track(appAnalytics.events.SP_ADD_VOUCHER_LINK, {
    item_id: selectedAccommodationId.value,
  })
  isVoucherOpen.value = true
}

const getClass = () => {
  const classes = []
  if (props.voucherData && props.voucherData !== null) {
    if (props.voucherData?.isValid) {
      classes.push('tw:border-green-700 tw:text-green-700')
    } else if (!props.voucherData?.isValid && code.value) {
      classes.push('tw:border-red-700')
    }
  }
  if (code.value && !props.price && !props.isLoading) {
    classes.push('tw:border-red-700')
  }
  return classes.join(' ')
}
</script>
<style scoped>
.voucher:hover svg,
.voucher:hover {
  fill: var(--tw-color-blue-800);
  color: var(--tw-color-blue-800);
}
</style>
